import logoL2m from '@assets/img/logo_l2m.png';
import logoLw from '@assets/img/logo_lw.png';
import heroBg1 from '@assets/img/main-bg1.jpg';
import heroBg2 from '@assets/img/main-bg2.jpg';
import { type GameId } from '@constants/index';
import { useCallback, useRef, useState, type TouchEvent } from 'react';

interface SlideData {
    bg: string;
    logo: string;
    logoAlt: string;
    ctaLabel: string;
    game: GameId;
    tagline: [string, string];
}

const SLIDES: SlideData[] = [
    {
        bg: heroBg2,
        logo: logoLw,
        logoAlt: 'Lineage W',
        ctaLabel: 'Download Now',
        game: 'lw',
        tagline: ['Creating a global playground ', 'where everyone belongs'],
    },
    {
        bg: heroBg1,
        logo: logoL2m,
        logoAlt: 'Lineage 2M',
        ctaLabel: 'Download Now',
        game: 'l2m',
        tagline: ['Creating a global playground ', 'where everyone belongs'],
    },
];

const MainSection = ({ onDownloadClick }: { onDownloadClick: (game: GameId) => void }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [animKey, setAnimKey] = useState(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const touchStartX = useRef(0);
    const sectionRef = useRef<HTMLElement>(null);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const goToSlide = useCallback((next: number) => {
        setCurrentSlide(next);
        setAnimKey((k) => k + 1);
    }, []);

    const handleSlideChange = useCallback(
        (next: number) => {
            clearTimer();
            goToSlide(next);
        },
        [clearTimer, goToSlide]
    );

    const handleTouchStart = useCallback((e: TouchEvent<HTMLElement>) => {
        touchStartX.current = e.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback(
        (e: TouchEvent<HTMLElement>) => {
            const diff = touchStartX.current - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
                const next = diff > 0 ? (currentSlide + 1) % SLIDES.length : (currentSlide - 1 + SLIDES.length) % SLIDES.length;
                handleSlideChange(next);
            }
        },
        [currentSlide, handleSlideChange]
    );

    const slide = SLIDES[currentSlide];

    return (
        <section
            ref={sectionRef}
            className="sec sec-main hero-carousel"
            id="main"
            aria-roledescription="carousel"
            aria-label="Hero"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'pan-y' }}
        >
            {/* Background slides */}
            <div className="hero__bg-container">
                {SLIDES.map((s, i) => (
                    <div key={i} className={`hero__bg-slide ${i === currentSlide ? 'active' : ''}`} aria-hidden={i !== currentSlide}>
                        <img
                            src={s.bg}
                            alt=""
                            className={`hero__bg-img ${i === currentSlide ? 'hero-animate-bg-zoom' : ''}`}
                            draggable={false}
                        />
                    </div>
                ))}
            </div>

            {/* Content overlay — key triggers re-mount for fresh animation on each slide */}
            <div className="hero__content" key={animKey}>
                <div className="hero__spacer" aria-hidden="true" />

                <div className="hero__promo">
                    <div className="hero__logo hero-animate-logo">
                        <img src={slide.logo} alt={slide.logoAlt} draggable={false} />
                    </div>
                    <div className="hero__cta hero-animate-cta">
                        <a
                            href="#"
                            className="hero__cta-button"
                            onClick={(e) => {
                                e.preventDefault();
                                onDownloadClick(slide.game);
                            }}
                        >
                            {slide.ctaLabel}
                            <span aria-hidden="true" className="hero__cta-arrow">
                                &rsaquo;
                            </span>
                        </a>
                    </div>
                </div>

                <div className="hero__bottom-spacer" aria-hidden="true" />

                <p className="hero__tagline hero-animate-cta">
                    <span className="hero__tagline-line">{slide.tagline[0]}</span>
                    <span className="hero__tagline-line">{slide.tagline[1]}</span>
                </p>

                {/* Slide indicators */}
                <div className="hero__indicators" role="tablist" aria-label="Hero slides">
                    {SLIDES.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            role="tab"
                            aria-selected={i === currentSlide}
                            className="hero__indicator-btn"
                            onClick={() => handleSlideChange(i)}
                        >
                            <span className={`hero__indicator-bar ${i === currentSlide ? 'active' : ''}`} aria-hidden="true" />
                        </button>
                    ))}
                </div>
            </div>

            <p className="sr-only" aria-live="polite">
                Slide {currentSlide + 1} of {SLIDES.length}
            </p>
        </section>
    );
};

export default MainSection;
